import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import FAQSection from "@/components/FAQSection";
import { Lightbulb, Play, Check } from "lucide-react";
import { challenges, approach, faqs } from "@/data/yourCommsData";

const YourCommsPage = () => {
  return (
    <div data-testid="your-comms-page" className="min-h-screen bg-[#050505]">
      <SEO
        title="Your Internal Communications Challenges — And How We Fix Them"
        description="Does your workforce struggle to find training, understand company values, or stay informed? Most organisations have the content — the problem is nobody sees it. Discover how ICA bridges the gap between what you have and what your people know."
        keywords="internal communications problems, employee engagement challenges, staff don't know company values, training not effective, onboarding problems, internal comms solutions, employee communication gaps"
        canonical="/your-comms"
      />

      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center" data-testid="your-comms-hero">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#050505] via-[#0a0a0a] to-[#050505]" />
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#00E5FF]/5 rounded-full blur-[200px]" />
        </div>

        <div className="container mx-auto px-4 md:px-8 relative z-10 pt-32">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-lg bg-[#00E5FF]/20 flex items-center justify-center">
                <Lightbulb className="w-6 h-6 text-[#00E5FF]" />
              </div>
              <span className="text-[#00E5FF] font-semibold uppercase tracking-wider">
                Your Comms
              </span>
            </div>

            <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-none text-white mb-6">
              YOU DON'T HAVE
              <br />
              A TRAINING PROBLEM
              <br />
              <span className="text-[#00E5FF]">YOU HAVE A COMMUNICATIONS PROBLEM</span>
            </h1>

            <p className="text-lg md:text-xl text-[#B3B3B3] max-w-3xl mb-4">
              Maybe the content exists but your people can't find it. Maybe it doesn't exist at all. 
              Either way, the result is the same — your workforce is disconnected from the information that matters.
            </p>
            <p className="text-xl md:text-2xl text-white font-semibold max-w-3xl mb-8">
              We fix both. We communicate what you have and create what you don't.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button
                  data-testid="your-comms-cta-btn"
                  className="bg-[#00E5FF] text-black hover:bg-[#00B8CC] font-bold px-8 py-6 uppercase tracking-wider text-base"
                >
                  <Play className="w-5 h-5 mr-2 fill-black" />
                  Let's Fix This
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Challenges & Solutions */}
      <section className="py-16 md:py-24" data-testid="challenges-solutions">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="font-display text-3xl md:text-4xl tracking-wide text-white mb-4">
              SOUND FAMILIAR?
            </h2>
            <p className="text-[#B3B3B3] text-lg">
              These are the challenges we hear from organisations every day — and exactly what we're built to solve.
            </p>
          </div>

          <div className="space-y-12 max-w-5xl mx-auto">
            {challenges.map((item, index) => (
              <div
                key={index}
                data-testid={`challenge-${index}`}
                className="bg-[#0a0a0a] border border-[#1F1F1F] rounded-xl overflow-hidden hover:border-[#00E5FF]/20 transition-colors"
              >
                <div className="grid grid-cols-1 lg:grid-cols-2">
                  <div className="p-8 border-b lg:border-b-0 lg:border-r border-[#1F1F1F]">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                        <item.icon className="w-5 h-5 text-red-400" />
                      </div>
                      <h3 className="font-display text-xl text-white">{item.title}</h3>
                    </div>
                    <p className="text-[#B3B3B3] leading-relaxed">{item.problem}</p>
                  </div>

                  <div className="p-8">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-lg bg-[#00E5FF]/10 flex items-center justify-center">
                        <Check className="w-5 h-5 text-[#00E5FF]" />
                      </div>
                      <h3 className="font-display text-xl text-[#00E5FF]">How We Fix This</h3>
                    </div>
                    <p className="text-[#B3B3B3] leading-relaxed">{item.solution}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Real Problem - moved after Sound Familiar */}
      <section className="py-16 md:py-20 bg-[#0a0a0a]" data-testid="real-problem">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-display text-3xl md:text-4xl tracking-wide text-white mb-6">
              THE REAL PROBLEM
            </h2>
            <p className="text-[#B3B3B3] text-lg leading-relaxed mb-4">
              It usually comes down to one of two things. Either the content already exists — training programmes, 
              values statements, safety documentation, employee handbooks — but nobody can find it, nobody engages 
              with it, and it's gathering dust on shared drives, buried intranets and all-staff emails that go 
              straight to the bin. The communication simply isn't working.
            </p>
            <p className="text-[#B3B3B3] text-lg leading-relaxed mb-4">
              Or the content doesn't exist at all. There's no onboarding video, no values campaign, no safety 
              training beyond a printed handout from five years ago. The ambition is there, but the resource, 
              expertise or time to produce it isn't.
            </p>
            <p className="text-[#B3B3B3] text-lg leading-relaxed">
              Either way, we've got you covered. If your content exists but isn't landing, we rebuild how 
              it's communicated — better formats, better channels, better strategy. If it doesn't exist yet, 
              we create it from scratch — professional video, design, photography, and copywriting, all 
              tailored to your workforce. One agency, both problems solved.
            </p>
          </div>
        </div>
      </section>

      {/* How We Deliver */}
      <section className="py-16 md:py-20 bg-[#0a0a0a]" data-testid="how-we-deliver">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl tracking-wide text-white mb-4">
              HOW WE DELIVER
            </h2>
            <p className="text-[#B3B3B3] text-lg">
              Everything you need to transform your internal communications, delivered by one agency.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {approach.map((item, index) => (
              <div
                key={index}
                className="p-6 bg-[#141414] rounded-lg border border-[#1F1F1F] hover:border-[#00E5FF]/30 transition-colors"
              >
                <div className="w-12 h-12 rounded-lg bg-[#00E5FF]/10 flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-[#00E5FF]" />
                </div>
                <h3 className="font-display text-lg text-white mb-2">{item.title}</h3>
                <p className="text-[#B3B3B3] text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQSection faqs={faqs} />

      {/* CTA */}
      <section className="py-20 md:py-32 bg-[#050505] relative overflow-hidden" data-testid="your-comms-cta">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#00E5FF] rounded-full blur-[150px]" />
        </div>

        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-display text-4xl md:text-5xl tracking-wide text-white mb-6">
              READY TO CLOSE
              <br />
              <span className="text-[#00E5FF]">THE COMMUNICATIONS GAP?</span>
            </h2>
            <p className="text-[#B3B3B3] text-lg mb-8">
              Your people deserve better than buried documents and forgotten emails. 
              Let's turn what you already have into communications that actually work.
            </p>
            <Link to="/contact">
              <Button
                data-testid="your-comms-final-cta"
                className="bg-[#00E5FF] text-black hover:bg-[#00B8CC] font-bold px-10 py-6 uppercase tracking-wider text-base"
              >
                Start the Conversation
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default YourCommsPage;
